import { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useSelfServeSignup } from 'api/useSelfServeSignup';
import { AxiosError } from 'axios';
import { GOOGLE_AUTH_CLIENT_ID } from 'constants/constants';
import { EDashboardPage, EEventsType } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { getAttributionData } from 'utils/eventsDataUtil';
import { isMixpanelInitialized, trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';

import Typography from 'components/Typography/Typography';

import AuthenticationLayout from './elements/AuthenticationLayout';
import SignUpForm from './elements/SignUpForm';
import { useSignUp } from './utils/useSignUp';

const SignUpPage = () => {
  const hasTracked = useRef(false);
  const navigate = useNavigate();
  const { verifyUser } = useSelfServeSignup();
  const location = useLocation();
  const { handlePostLogin } = useSignUp();
  const { trackBigQueryEvent, trackPreLoginEvent } = useBigQueryEventsTracking();
  const params = new URLSearchParams(location.search);
  const userId = params.get('userId');
  const token = params.get('token');

  useEffect(() => {
    if (isMixpanelInitialized() && !hasTracked.current) {
      const attributionData = getAttributionData();
      
      trackMixpanelEvent(EEventsType.DASHBOARD_SIGNUP_LANDED, {
        ...attributionData
      });

      trackPreLoginEvent({
        page: EDashboardPage.SIGNUP,
        eventName: EEventsType.DASHBOARD_SIGNUP_LANDED,
        ...(userId && { userId })
      });
      
      hasTracked.current = true;
    }
  }, [userId])

  useEffect(() => {
    if (
      userId &&
      token &&
      !verifyUser.isLoading &&
      !verifyUser.isSuccess &&
      !verifyUser.isError
    ) {
      trackMixpanelEvent(EEventsType.DASHBOARD_EMAIL_VERIFICATION_CLICKED, {
        user_id: userId
      });

      trackBigQueryEvent({
        eventName: EEventsType.DASHBOARD_EMAIL_VERIFICATION_CLICKED,
        userId
      });
      verifyUser.mutate(
        {
          userId,
          verificationToken: token
        },
        {
          onSuccess: (data) => {
            handlePostLogin(data);
          },
          onError: (error) => {
            showToast({
              message:
                'Verification failed. Please try again or contact support.',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError)?.response?.data as {
                    [key: string]: any;
                  }
                )?.message ?? 'Unknown error occurred'
            });
            setTimeout(() => {
              navigate('/sign-up');
            }, 1500);
          }
        }
      );
    }
  }, [userId, token, verifyUser, handlePostLogin, navigate]);

  if (userId && token && verifyUser.isLoading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <CircularProgress
          className="h-16 w-16"
          sx={{ color: 'var(--color-primary)' }}
        />
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_AUTH_CLIENT_ID}>
      <AuthenticationLayout
        title="Payment Links"
        main={<SignUpForm />}
        footer={
          <Typography className="text-p-s text-zinc-500 text-center">
            <div>
              By signing up, you agree to{' '}
              <span className="font-bold">Appcharge</span>
            </div>
            <div>
              <a
                href="https://www.appcharge.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://www.appcharge.com/tc"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Terms of Service.
              </a>
            </div>
          </Typography>
        }
      />
    </GoogleOAuthProvider>
  );
};

export default SignUpPage;
